import { v4 as uuidv4 } from 'uuid';
import { processingStore } from '@shared/openlayers/tools/volume/utils/store/processing-store';
import Feature from 'ol/Feature';
import { Geometry } from 'ol/geom';
import {
    StockpileResponse,
    ComparisonResponse,
    ComparisonApiParams,
    VariationApiParams,
    Measurement,
    demApiParams,
    MergeVolumeApiParams,
    GetStockpileVolumeParams,
    HandleSegmentParams,
    segmentResult,
    CutFillParams,
    cutFillResult,
    CalcModelDEMVolumeParams,
    AlignmentSegment,
} from '../../../../../typings/reality-capture/volumes/models';
import { stockpileApi, comparisonApi, mergeVolumeApi, VariationApi } from './api';
import { splitOpenlayersFeatureIntoSegments, determineTargetArea, splitCorridorByCenterline } from '@shared/openlayers/tools/volume/utils/helpers/helpers';
import type { CorridorSide } from '@shared/openlayers/tools/volume/utils/helpers/helpers';
import { KmlExport } from '@openlayers/tools/KmlExport';
import { calcArea } from '@shared/openlayers/tools/tool-utils';
import { RateLimiter } from '@shared/utils/rate-limiter';

export async function getStockpileVolume(params: GetStockpileVolumeParams): Promise<Measurement> {
    const { sourceProj, feature, customBaseHeight, tolerance, calcReference } = params;
    const { area, prefix } = calcArea(feature.getGeometry(), sourceProj);
    const kmlFile = KmlExport.getFile([feature]);

    try {
        // Determine target layer
        const apiParams: demApiParams = {
            dem_url: calcReference.surfaceDemUrl,
            area: area,
            kml_segment: kmlFile,
            customBaseHeight: customBaseHeight,
            tolerance: tolerance,
        };
        const response: StockpileResponse = await stockpileApi(apiParams);

        const measurement: Measurement = {
            uuid: uuidv4(),
            type: 'stockpile',
            cut_volume: response.cut_volume,
            fill_volume: response.fill_volume,
            area: area,
            calcReference: calcReference,
            viz_surface: response.viz_surface,
            feature: feature,
            baseHeight: response.base_height,
            tolerance: response.tolerance,
            colors: response.color,
            highest_point: response.highest_point,
            lowest_point: response.lowest_point,
            failure_rate: response.failure_rate,
        };
        return measurement;
    } catch (error) {
        console.error('Error calculating stockpile volume:', error);
        return null;
    }
}

export async function getComparisonVolume(params: CalcModelDEMVolumeParams): Promise<Measurement> {
    const { sourceProj, feature, tolerance, cacheUuid, calcReference, calcTarget } = params;

    // Get KML file for the feature
    const kml_file: Blob = KmlExport.getFile([feature]);

    try {
        // Get variation
        processingStore.startPart(10, 1, cacheUuid);
        const variationParams: VariationApiParams = {
            reference: calcReference,
            target: calcTarget,
            file: kml_file,
            tolerance: tolerance,
        };
        const variation = await VariationApi(variationParams);

        const cutFillParams: CutFillParams = {
            feature,
            sourceProj,
            cacheUuid,
            tolerance,
            calcReference,
            calcTarget,
            variation,
        };
        const { cutVolume, fillVolume, alignmentSegments } = await getCutFill(cutFillParams);

        processingStore.startPart(100, 1, cacheUuid);
        const mergeParams: MergeVolumeApiParams = {
            cache_uuid: cacheUuid,
            project_uuid: kuura.getProjectUuid(),
            clear_cache: true,
        };
        const surfaces = await mergeVolumeApi(mergeParams);

        const { area, prefix } = calcArea(feature.getGeometry(), sourceProj);

        // Set processing completed
        processingStore.stopPart(100, cacheUuid);
        processingStore.setCompleted(cacheUuid);

        const measurement: Measurement = {
            uuid: uuidv4(),
            type: 'comparison',
            fill_volume: fillVolume,
            cut_volume: cutVolume,
            area: area,
            calcReference: calcReference,
            calcTarget: calcTarget,
            viz_surface: surfaces,
            feature: feature,
            tolerance: tolerance,
            colors: variation.color,
            highest_point: variation.max_z,
            lowest_point: variation.min_z,
            failure_rate: variation.failure_rate,
            alignmentSegments,
        };
        return measurement;
    } catch (error) {
        console.error('Error calculating comparison volume:', error);
        return null;
    }
}

const ALIGNMENT_SECTION_LENGTH = 10;

async function getCutFill(params: CutFillParams): Promise<cutFillResult> {
    const { feature, sourceProj, cacheUuid, tolerance, calcReference, calcTarget, variation } = params;

    // For alignment-based features, split into 10 m sections along the centerline.
    // For manually drawn areas, fall back to the grid-based area splitter.
    const alignmentEnPoints = feature.get('alignmentEnPoints') as number[][] | undefined;
    const alignmentCorridorWidth = feature.get('alignmentCorridorWidth') as number | undefined;
    const alignmentStartStation = feature.get('alignmentStartStation') as number | undefined;
    const alignmentSide = (feature.get('alignmentSide') as CorridorSide | undefined) ?? 'both';
    const isAlignmentBased = !!(alignmentEnPoints && alignmentEnPoints.length >= 2 && alignmentCorridorWidth);

    const segments: Feature<Geometry>[] = isAlignmentBased
        ? splitCorridorByCenterline(alignmentEnPoints!, alignmentCorridorWidth!, ALIGNMENT_SECTION_LENGTH, alignmentSide)
        : splitOpenlayersFeatureIntoSegments(feature, determineTargetArea(calcTarget.surfaceType, calcReference.surfaceType));

    // Start processing segments
    processingStore.startPart(80, segments.length, cacheUuid);

    // Limit concurrent API calls using shared RateLimiter
    const limiter = new RateLimiter(10); // adjust concurrency as needed

    // Get calculation results for each segment with concurrency control
    const segmentPromises = segments.map(segment => {
        const segmentParams: HandleSegmentParams = {
            segment,
            sourceProj,
            cacheUuid,
            tolerance,
            calcReference,
            calcTarget,
            variation,
        };
        return (async () => {
            const release = await limiter.requestToken();
            try {
                return await handleSegment(segmentParams);
            } catch (error) {
                console.error('Error calculating volume for segment:', error);
                throw error;
            } finally {
                release();
                // Update processing pipeline status
                processingStore.tick(cacheUuid);
            }
        })();
    });
    const results = await Promise.all(segmentPromises);

    // Aggregate results from all segments
    let fillVolume = 0;
    let cutVolume = 0;
    for (const r of results) {
        fillVolume += r.segmentFillVolume;
        cutVolume += r.segmentCutVolume;
    }

    // For alignment-based calculations, attach per-section station data for the chart
    const alignmentSegments: AlignmentSegment[] | undefined = isAlignmentBased
        ? results.map(
              (r, i): AlignmentSegment => ({
                  station: (alignmentStartStation ?? 0) + i * ALIGNMENT_SECTION_LENGTH,
                  fillVolume: r.segmentFillVolume,
                  cutVolume: r.segmentCutVolume,
              })
          )
        : undefined;

    return { fillVolume, cutVolume, alignmentSegments };
}

async function handleSegment(params: HandleSegmentParams): Promise<segmentResult> {
    const { segment, sourceProj, cacheUuid, tolerance, calcReference, calcTarget, variation } = params;
    // Calculate area of the segment
    const { area, prefix } = calcArea(segment.getGeometry(), sourceProj);

    // If area is negligible, return zero volumes
    if (area < 0.1) {
        return { segmentFillVolume: 0, segmentCutVolume: 0 };
    }

    // Convert segment to file
    const kml_segment = KmlExport.getFile([segment]);

    // Get API response for the segment
    const comparisonParams: ComparisonApiParams = {
        cacheUuid,
        area,
        kml_segment,
        tolerance,
        target: calcTarget,
        reference: calcReference,
        variation,
    };
    const response: ComparisonResponse = await comparisonApi(comparisonParams);

    // Generate segmentResult object
    const result: segmentResult = {
        segmentFillVolume: response.fill_volume,
        segmentCutVolume: response.cut_volume,
    };
    return result;
}
